import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, CalendarX, ExternalLink } from "lucide-react";

export default function ContactSection() {
    // Varianti per l'animazione a cascata (stagger) dei box info
    const containerVariants = {
        hidden: {},
        visible: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: "easeOut" }
        }
    };

    return (
        <section className="relative px-6 py-24 bg-[#0D121F] border-t border-accent/20 overflow-hidden">
            {/* Soft glow d'atmosfera nell'angolo in basso a destra */}
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

            <div className="max-w-5xl mx-auto space-y-16">

                {/* Intestazione Sezione */}
                <div className="text-center space-y-2">
                    <span className="text-primary text-xs font-black tracking-widest uppercase block">Contatti & Prenotazioni</span>
                    <h2 className="text-3xl md:text-4xl font-title text-secondary tracking-tight">
                        Vieni a Trovarci
                    </h2>
                    <div className="w-10 h-[2px] bg-primary/60 rounded-full mx-auto pt-[2px]" />
                </div>

                {/* 🚀 LAYOUT CORE: Due colonne asimmetriche stabili su Desktop */}
                <div className="flex flex-col lg:flex-row items-stretch gap-8">

                    {/* Colonna Sinistra: Informazioni Utili */}
                    <motion.div
                        className="w-full lg:w-5/12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        {/* Box: Dove Siamo */}
                        <motion.div variants={itemVariants} className="bg-[#090D16] p-5 rounded-2xl border border-accent/15 flex gap-4 items-center group hover:border-primary/30 transition-colors duration-300">
                            <div className="p-3 bg-primary/10 rounded-xl text-primary shrink-0">
                                <MapPin className="w-5 h-5" />
                            </div>
                            <div className="min-w-0">
                                <h4 className="text-[10px] font-black text-muted/60 uppercase tracking-widest">Dove Siamo</h4>
                                <p className="text-sm font-medium text-secondary">Via Piave, 13 - Avetrana</p>
                            </div>
                        </motion.div>

                        {/* Box: Telefono */}
                        <motion.div variants={itemVariants} className="bg-[#090D16] p-5 rounded-2xl border border-accent/15 flex gap-4 items-center group hover:border-primary/30 transition-colors duration-300">
                            <div className="p-3 bg-primary/10 rounded-xl text-primary group-hover:bg-primary group-hover:text-black transition-all duration-300 shrink-0">
                                <Phone className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-[10px] font-black text-muted/60 uppercase tracking-widest">Contatto Diretto</h4>
                                <a href="tel:+393501175128" className="text-sm font-medium text-secondary hover:text-primary transition-colors font-mono">
                                    +39 350 117 5128
                                </a>
                            </div>
                        </motion.div>

                        {/* Box: Orari */}
                        <motion.div variants={itemVariants} className="bg-[#090D16] p-5 rounded-2xl border border-accent/15 flex gap-4 items-center group hover:border-primary/25 transition-colors duration-300">
                            <div className="p-3 bg-primary/10 rounded-xl text-primary shrink-0">
                                <Clock className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-[10px] font-black text-muted/60 uppercase tracking-widest">Orari di Apertura</h4>
                                <p className="text-sm font-medium text-secondary">Ogni sera 19:00 — 00:00</p>
                            </div>
                        </motion.div>

                        {/* Box: Chiusura */}
                        <motion.div variants={itemVariants} className="bg-[#090D16] p-5 rounded-2xl border border-accent/15 flex gap-4 items-center group hover:border-primary/25 transition-colors duration-300">
                            <div className="p-3 bg-red-500/10 rounded-xl text-red-400 shrink-0">
                                <CalendarX className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-[10px] font-black text-muted/60 uppercase tracking-widest">Turno di Riposo</h4>
                                <p className="text-sm font-bold text-red-400/90">Chiuso il Mercoledì</p>
                            </div>
                        </motion.div>
                    </motion.div>

                    {/* Colonna Destra: Mappa Interattiva */}
                    <motion.div
                        className="w-full lg:w-7/12 rounded-3xl overflow-hidden border border-accent/20 shadow-2xl bg-[#090D16] relative flex flex-col items-center justify-center p-8 md:p-12 min-h-[340px] group text-center"
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        {/* Sfondo dinamico con sfumature lussuose che reagiscono all'hover */}
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-black/50 z-0 pointer-events-none" />
                        <div className="absolute -inset-px bg-gradient-to-tr from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl pointer-events-none" />

                        {/* Icona del Pin che pulsa e si ingrandisce all'hover della card */}
                        <div className="relative z-10 mb-6 p-4 bg-[#0D121F] border border-accent/20 rounded-2xl text-primary shadow-xl group-hover:scale-110 group-hover:border-primary/30 transition-all duration-300">
                            <MapPin className="w-8 h-8 animate-pulse" />
                        </div>

                        {/* Testo persuasivo per invitare l'utente al locale */}
                        <div className="relative z-10 max-w-sm space-y-3 mb-8">
                            <h3 className="text-xl md:text-2xl font-title text-secondary tracking-tight">
                                Come raggiungerci
                            </h3>
                            <p className="text-muted text-sm leading-relaxed">
                                Ci trovi nel cuore di Avetrana. Clicca qui sotto per avviare il tuo navigatore preferito e raggiungerci senza sforzo.
                            </p>
                        </div>

                        {/* Pulsante di navigazione con effetto glow coordinato */}
                        <a
                            href="https://goo.gl/maps/f7yhpAyxAPCeKrDL9"
                            target="_blank"
                            rel="noreferrer"
                            className="relative z-10 w-full sm:w-auto px-8 py-4 rounded-full bg-primary text-black font-black tracking-wide text-sm flex items-center justify-center gap-2 shadow-lg shadow-primary/10 hover:shadow-[0_0_30px_rgba(233,69,69,0.3)] hover:scale-105 active:scale-95 transition-all duration-300 group/btn"
                        >
                            OTTIENI INDICAZIONI
                            {/* L'icona si sposta leggermente in alto a destra quando passi sul bottone */}
                            <ExternalLink className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                        </a>
                    </motion.div>

                </div>
            </div>
        </section>
    );
}